import { z } from "zod";
import { SERVICE_DISCOUNT_KINDS } from './sales.js';
import {
  CONTRACT_STATUSES,
  INVOICE_STATUSES,
  QUOTATION_STATUSES,
} from "./sales.js";
import { CuidSchema, ListQuerySchema } from "./crm.schemas.js";

/**
 * مخططات وحدة المبيعات والحسابات.
 */

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional();

const money = z
  .number()
  .min(0, "القيمة لا يمكن أن تكون سالبة.")
  .max(99_999_999_999.99, "القيمة أكبر من الحد المسموح.");

const optionalDate = z.coerce.date().nullable().optional();

/* ==========================================================================
   كتالوج الخدمات
   ========================================================================== */

export const ServiceListQuerySchema = ListQuerySchema.extend({
  category: z.string().trim().max(80).optional(),
  /* لا coerce هنا: النصّ "false" كان سيصير صحيحًا فيقلب المرشّح. */
  isActive: z
    .union([z.boolean(), z.enum(["true", "false"])])
    .transform((v) => v === true || v === "true")
    .optional(),
});
export type ServiceListQuery = z.infer<typeof ServiceListQuerySchema>;

export const CreateServiceSchema = z.object({
  /* الكودُ يُولَّد إن تُرك فارغًا — ويُقبل مكتوبًا لمن يرقّم بنظامه. */
  code: optionalText(40),
  name: z.string().trim().min(2, "اسم الخدمة مطلوب.").max(160),
  description: optionalText(1000),
  category: optionalText(80),
  department: optionalText(80),
  /* غلافٌ مضمَّنٌ — الحدُّ يسع صورةً مضغوطةً ولا يسع ملفًّا خامًّا. */
  imageUrl: z.string().trim().max(700_000).nullable().optional(),
  unit: z.string().trim().min(1).max(40).default("خدمة"),
  unitPrice: money.default(0),
  unitCost: money.nullable().optional(),
  taxable: z.boolean().default(true),
  isActive: z.boolean().optional(),

  discountKind: z.enum(SERVICE_DISCOUNT_KINDS).default("NONE"),
  discountValue: money.nullable().optional(),
  discountFrom: z.coerce.date().nullable().optional(),
  discountTo: z.coerce.date().nullable().optional(),
})
  /*
   * خصمٌ بلا قيمةٍ خصمٌ لا يخصم.
   *
   * حفظُه يُظهر شارةَ خصمٍ على الخدمة ولا يغيّر سعرها، فيُسأل: «لماذا لم
   * ينزل؟». فيُرفض عند الحفظ لا بعد شهرٍ من العروض.
   */
  .refine(
    (v) => v.discountKind === "NONE" || (v.discountValue ?? 0) > 0,
    { message: "اكتب قيمة الخصم.", path: ["discountValue"] },
  )
  .refine(
    (v) => v.discountKind !== "PERCENT" || (v.discountValue ?? 0) <= 100,
    { message: "نسبة الخصم لا تتجاوز مئة.", path: ["discountValue"] },
  )
  .refine(
    (v) =>
      !v.discountFrom ||
      !v.discountTo ||
      new Date(v.discountTo) >= new Date(v.discountFrom),
    { message: "تاريخ نهاية الخصم قبل بدايته.", path: ["discountTo"] },
  );
export type CreateServiceInput = z.infer<typeof CreateServiceSchema>;

/*
 * التعديلُ لا يرث تحقّقات الإنشاء.
 *
 * ‏«partial» لا تعمل على مخطّطٍ عليه «refine»، ولأنّ التعديل قد يرسل
 * حقلًا واحدًا فالتحقّقُ المتقاطع لا يصحّ عليه أصلًا — يُفحص الخصمُ كاملًا
 * في الخدمة بعد دمجه بالمحفوظ.
 */
export const UpdateServiceSchema = z.object({
  code: optionalText(40),
  name: z.string().trim().min(2, "اسم الخدمة مطلوب.").max(160).optional(),
  description: optionalText(1000),
  category: optionalText(80),
  department: optionalText(80),
  imageUrl: z.string().trim().max(700_000).nullable().optional(),
  unit: z.string().trim().min(1).max(40).optional(),
  unitPrice: money.optional(),
  unitCost: money.nullable().optional(),
  taxable: z.boolean().optional(),
  isActive: z.boolean().optional(),
  discountKind: z.enum(SERVICE_DISCOUNT_KINDS).optional(),
  discountValue: money.nullable().optional(),
  discountFrom: z.coerce.date().nullable().optional(),
  discountTo: z.coerce.date().nullable().optional(),
});
export type UpdateServiceInput = z.infer<typeof UpdateServiceSchema>;

/* ==========================================================================
   بنود المستندات
   ========================================================================== */

export const DocumentLineSchema = z.object({
  serviceId: CuidSchema.nullable().optional(),
  description: z.string().trim().min(1, "وصف البند مطلوب.").max(500),
  quantity: z.number().positive("الكمية يجب أن تكون أكبر من صفر.").max(999_999),
  unit: z.string().trim().min(1).max(40).default("خدمة"),
  unitPrice: money,
  discount: money.default(0),
});
export type DocumentLineInput = z.infer<typeof DocumentLineSchema>;

/**
 * قائمة البنود.
 *
 * لا نقبل مستندًا بلا بنود: مستند بإجمالي صفر وبلا تفصيل لا يمكن مراجعته
 * ولا الاعتراض عليه، ويصير مصدر خلاف مع العميل لا مرجعًا.
 */
const LinesSchema = z
  .array(DocumentLineSchema)
  .min(1, "يجب إضافة بند واحد على الأقل.")
  .max(200, "عدد البنود أكبر من الحد المسموح.");

/* ==========================================================================
   عروض الأسعار
   ========================================================================== */

export const QuotationListQuerySchema = ListQuerySchema.extend({
  status: z.enum(QUOTATION_STATUSES).optional(),
  accountId: CuidSchema.optional(),
  leadId: CuidSchema.optional(),
  dealId: CuidSchema.optional(),
});
export type QuotationListQuery = z.infer<typeof QuotationListQuerySchema>;

/** العرضُ على حسابٍ أو على محتمل — أحدُهما، والخدمةُ تتحقّق. */
export const CreateQuotationSchema = z.object({
  accountId: CuidSchema.optional(),
  leadId: CuidSchema.optional(),
  dealId: CuidSchema.nullable().optional(),
  title: optionalText(200),
  notes: optionalText(2000),
  terms: optionalText(2000),
  validUntil: optionalDate,
  discount: money.default(0),
  /*
   * لا قيمة افتراضية هنا عمدًا.
   *
   * وضع 14 يجعل الخادم عاجزًا عن التمييز بين طلب أغفل النسبة وطلب أرسلها
   * أربعة عشر، فيتجاهل نسبة المستأجر المضبوطة في إعداداته دائمًا.
   */
  taxRate: z.number().min(0).max(100).optional(),
  lines: LinesSchema,
});
export type CreateQuotationInput = z.infer<typeof CreateQuotationSchema>;

export const UpdateQuotationSchema = CreateQuotationSchema.partial().omit({
  accountId: true,
  leadId: true,
});
export type UpdateQuotationInput = z.infer<typeof UpdateQuotationSchema>;

export const DecideQuotationSchema = z
  .object({
    decision: z.enum(["ACCEPTED", "REJECTED"]),
    rejectReason: optionalText(300),
  })
  .refine((v) => v.decision !== "REJECTED" || !!v.rejectReason, {
    message: "سبب الرفض مطلوب.",
    path: ["rejectReason"],
  });
export type DecideQuotationInput = z.infer<typeof DecideQuotationSchema>;

/* ==========================================================================
   العقود
   ========================================================================== */

export const ContractListQuerySchema = ListQuerySchema.extend({
  status: z.enum(CONTRACT_STATUSES).optional(),
  accountId: CuidSchema.optional(),
});
export type ContractListQuery = z.infer<typeof ContractListQuerySchema>;

export const MilestoneSchema = z.object({
  title: z.string().trim().min(1).max(200),
  amount: money,
  dueAt: optionalDate,
  done: z.boolean().default(false),
});

/**
 * بند العقد.
 *
 * الترتيب هو موضعه في المصفوفة لا رقمٌ يُحفظ معه: رقمٌ مخزَّن يفترق عن
 * الترتيب عند أول إدراجٍ في الوسط، فيصير في العقد بندان «ثالث».
 */
export const ClauseSchema = z.object({
  /** معرّفٌ محلّي يثبت عبر إعادة الترتيب، فلا يفقد الحقلُ تركيزه أثناء الكتابة. */
  id: z.string().trim().min(1).max(40),
  title: z.string().trim().max(200).default(""),
  body: z.string().trim().min(1, "نصّ البند مطلوب.").max(8000),
});
export type Clause = z.infer<typeof ClauseSchema>;

export const CreateContractSchema = z.object({
  accountId: CuidSchema,
  dealId: CuidSchema.nullable().optional(),
  quotationId: CuidSchema.nullable().optional(),
  title: z.string().trim().min(2, "عنوان العقد مطلوب.").max(200),
  startAt: z.coerce.date(),
  endAt: optionalDate,
  value: money.default(0),
  terms: optionalText(4000),
  notes: optionalText(2000),
  milestones: z.array(MilestoneSchema).max(50).default([]),
  clauses: z.array(ClauseSchema).max(100).default([]),
});

/** إنشاء عقدٍ من عرض سعرٍ مقبول — تُنسخ منه بياناته وبنودُه وقيمتُه. */
export const ContractFromQuotationSchema = z.object({
  quotationId: CuidSchema,
  title: z.string().trim().min(2).max(200).optional(),
  startAt: z.coerce.date().optional(),
  endAt: optionalDate,
  /** إدراج بنود العرض خدمةً خدمةً كبنودٍ في العقد. */
  includeLines: z.boolean().default(true),
  /** إدراج بنود العقد الافتراضية من الإعدادات. */
  includeDefaults: z.boolean().default(true),
});
export type ContractFromQuotationInput = z.infer<
  typeof ContractFromQuotationSchema
>;
export type CreateContractInput = z.infer<typeof CreateContractSchema>;

export const UpdateContractSchema = CreateContractSchema.partial()
  .omit({ accountId: true })
  .extend({
    status: z.enum(CONTRACT_STATUSES).optional(),
    /**
     * سبب تخويل التفعيل رغم تعثّر العميل ائتمانيًا.
     *
     * ليس حقلًا في العقد: يُقرأ عند التفعيل ويُسجَّل في سجل التخويلات ثم
     * يُنزع قبل الكتابة.
     */
    overrideReason: z
      .string()
      .trim()
      .min(5, "اذكر سبب التخويل في خمسة أحرف فأكثر.")
      .max(300)
      .optional(),
  });
export type UpdateContractInput = z.infer<typeof UpdateContractSchema>;

/* ==========================================================================
   الفواتير
   ========================================================================== */

export const InvoiceListQuerySchema = ListQuerySchema.extend({
  status: z.enum(INVOICE_STATUSES).optional(),
  accountId: CuidSchema.optional(),
  /** غير المسدَّدة بالكامل فقط. */
  unpaidOnly: z.coerce.boolean().optional(),
  overdueOnly: z.coerce.boolean().optional(),
  /** مدى تاريخ الإصدار (سنة-شهر-يوم) — والطرف الأخير داخلٌ يومُه كلّه. */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "التاريخ يجب أن يكون بصيغة سنة-شهر-يوم.").optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "التاريخ يجب أن يكون بصيغة سنة-شهر-يوم.").optional(),
});
export type InvoiceListQuery = z.infer<typeof InvoiceListQuerySchema>;

export const CreateInvoiceSchema = z.object({
  accountId: CuidSchema,
  dealId: CuidSchema.nullable().optional(),
  quotationId: CuidSchema.nullable().optional(),
  contractId: CuidSchema.nullable().optional(),
  title: optionalText(200),
  notes: optionalText(2000),
  terms: optionalText(2000),
  dueAt: optionalDate,
  discount: money.default(0),
  /*
   * لا قيمة افتراضية هنا عمدًا.
   *
   * وضع 14 يجعل الخادم عاجزًا عن التمييز بين طلب أغفل النسبة وطلب أرسلها
   * أربعة عشر، فيتجاهل نسبة المستأجر المضبوطة في إعداداته دائمًا.
   */
  taxRate: z.number().min(0).max(100).optional(),
  lines: LinesSchema,
});
export type CreateInvoiceInput = z.infer<typeof CreateInvoiceSchema>;

export const UpdateInvoiceSchema = CreateInvoiceSchema.partial().omit({
  accountId: true,
});
export type UpdateInvoiceInput = z.infer<typeof UpdateInvoiceSchema>;

/* ==========================================================================
   التحصيلات
   ========================================================================== */

export const PaymentListQuerySchema = ListQuerySchema.extend({
  accountId: CuidSchema.optional(),
  invoiceId: CuidSchema.optional(),
  method: z.string().trim().max(60).optional(),
  /* تحصيلات فريق المبيعات وحالة اعتمادها — لقائمة المراجعة. */
  recordedVia: z.enum(["FINANCE", "SALES"]).optional(),
  reviewStatus: z.enum(["PENDING", "CONFIRMED"]).optional(),
});
export type PaymentListQuery = z.infer<typeof PaymentListQuerySchema>;

export const CreatePaymentSchema = z.object({
  accountId: CuidSchema,
  /** الفاتورة المسدَّدة، أو لا شيء إن كانت دفعة تحت الحساب. */
  invoiceId: CuidSchema.nullable().optional(),
  amount: money.refine((v) => v > 0, "المبلغ يجب أن يكون أكبر من صفر."),
  paidAt: z.coerce.date().default(() => new Date()),
  method: optionalText(60),
  reference: optionalText(120),
  notes: optionalText(1000),
  /** الحساب النقدي الذي دخل إليه المال — من خزائن النظام الحقيقية. */
  cashAccountId: CuidSchema.nullable().optional(),
  /**
   * لخزينة بعملة أجنبية: المبلغ بعملتها وسعر الصرف. المبلغ بالجنيه يُحسب
   * منهما على الخادم ويُخصم من الفاتورة.
   */
  fxAmount: z.number().positive().max(99_999_999_999.99).nullable().optional(),
  fxRate: z.number().positive().max(1_000_000).nullable().optional(),
  /** SALES حين تُسجَّل من ملف العميل — فتولد بانتظار اعتماد المحاسب. */
  recordedVia: z.enum(["FINANCE", "SALES"]).default("FINANCE"),
});
export type CreatePaymentInput = z.infer<typeof CreatePaymentSchema>;
